import { PrintContextService } from '@/services/print-templates/print-context.service';
import { BaseTableService } from '@/services/ui/table/base-table.service';
import { Directive, effect, inject, input, OnDestroy } from '@angular/core';

interface IdentifiedRow {
  id?: string | null;
}

/**
 * Makes a table printable: list templates print from the navbar button (the
 * selected rows, or every row shown), record templates from a button in each
 * expanded row, which needs the subject set here.
 *
 * `<app-base-table [table]="table" appPrintableList="order" [printTable]="table" />`
 *
 * Detail pages use `appPrintableRecord` instead.
 */
@Directive({
  selector: '[appPrintableList]',
  standalone: true,
})
export class PrintableListDirective implements OnDestroy {
  /** Trigger subject of the rows, e.g. `order`, `traceable_entity`. */
  readonly appPrintableList = input.required<string>();
  // Row types differ per page; only `id` is read.
  readonly printTable = input.required<BaseTableService<any>>();

  private readonly printContext = inject(PrintContextService);

  constructor() {
    effect(() => {
      const table = this.printTable();
      const subject = this.appPrintableList();
      table.printSubject.set(subject);
      this.printContext.register(this, {
        subject,
        scope: 'list',
        ids: () => printableIds(table),
      });
    });
  }

  ngOnDestroy(): void {
    this.printTable().printSubject.set(null);
    this.printContext.unregister(this);
  }
}

/** Record ids are UUIDs; grouping rows (e.g. inventory groups) are skipped. */
const RECORD_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isPrintableRecordId(id: unknown): id is string {
  return typeof id === 'string' && RECORD_ID_RE.test(id);
}

function printableIds(table: BaseTableService<IdentifiedRow>): string[] {
  const selected = [...table.selectedRows()];
  const rows = selected.length ? selected : table.data();
  return rows.map(row => row?.id).filter(isPrintableRecordId);
}
